import {
  GraphQLInterfaceType,
  GraphQLObjectType,
  GraphQLSchema,
  isEnumType,
  isScalarType,
} from 'graphql';
import * as jsoncParser from 'jsonc-parser';
import { get, set, unset } from 'lodash';
import * as monacoEditor from 'monaco-editor';

import {
  SelectionPathNode,
  isGraphqlFieldPath,
} from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { docsPathToVariableJSONPath } from 'src/app/graph/explorerPage/helpers/jsonHelpers';
import { GraphQLPath } from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { JSONValue } from 'src/app/graph/explorerPage/helpers/types';
import { TrackingEvent } from 'src/hooks/useTrackCustomEvent';
import {
  GraphQLArgumentNode,
  GraphQLNode,
  getRootTypeNames,
  unwrapType,
} from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { EditorBottomTab } from '../../helpers/EditorBottomTab';

import { executeEdits } from './executeEdits';
import {
  CurrentDocsContext,
  Selection,
  getCurrentDocsContext,
} from './getCurrentDocsContext';
import {
  getAllFieldsDeletion,
  getArgumentDeletion,
  getDeletion,
} from './getDeletion';
import {
  getAllCurrentScalars,
  getAllFields,
  getArgumentsInsertion,
  getInsertion,
  printInnerSnippet,
  printInnerSnippetRecursive,
} from './getInsertion';

export type FieldToggleType =
  | {
      isInOperation: (node: SelectionPathNode) => boolean;
      getFieldInOperation: (node: SelectionPathNode) => Selection | undefined;
      isAnyFieldSelected: () => boolean;
      isAnyTypeSelected: () => boolean;
      areAllFieldsSelected: (node: SelectionPathNode) => boolean;
      areAllTypeFieldsSelected: (type: SelectionPathNode) => boolean;
      isArgumentPathValueSet: (argumentPath: GraphQLArgumentNode[]) => boolean;
      getArgumentInOperation: (
        argumentDocNode: GraphQLArgumentNode,
      ) => undefined | CurrentDocsContext['arguments'][number];
      toggleInOperation: (node: SelectionPathNode) => void;
      toggleAllFields: (node: SelectionPathNode) => void;
      insertTypeFields: (type: SelectionPathNode) => void;
      insertAllCurrentScalars: (node: SelectionPathNode) => void;
      insertTypeScalars: (type: SelectionPathNode) => void;
      insertAllFieldsRecursively: (
        node: SelectionPathNode,
        includeNode: boolean,
      ) => void;
      insertTypeFieldsRecursively: (type: SelectionPathNode) => void;
      toggleArgumentPathInOperation: (
        argumentPath: GraphQLArgumentNode[],
      ) => void;
      deleteAllTypes: (types: Array<SelectionPathNode>) => void;
    }
  | null
  | undefined;

const getFieldInOperationHelper = (
  currentDocsContext: CurrentDocsContext,
  node: SelectionPathNode,
) => {
  // There was a root mismatch or still further to go in the docs, nothing
  // should be checked
  if (
    currentDocsContext.matchType !== 'full' &&
    currentDocsContext.matchType !== 'root'
  ) {
    return undefined;
  }

  // We have a current node, and are at the end of our docs path
  return currentDocsContext.selectionSet.find((field) => {
    if (
      node instanceof GraphQLObjectType ||
      node instanceof GraphQLInterfaceType
    ) {
      return (
        field.name === node.name &&
        (field.type === 'inline fragment' || field.type === 'operation')
      );
    }
    return field.name === node.name && field.type === 'field';
  });
};

/**
 * Returns a function to if a given field is part of the operation, and a
 * function to toggle whether a field is in an operation.
 */
export function getFieldToggle(
  inputCurrentDocsContext: CurrentDocsContext,
  docsPath: GraphQLPath,
  schema: GraphQLSchema,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor,
  variablesEditorPosition: monacoEditor.Position | null | undefined,
  setActiveEditorBottomTab: (tab: EditorBottomTab) => void,
  shouldManageVariables: boolean,
  trackCustomEvent: (event: TrackingEvent) => void,
): FieldToggleType {
  let currentDocsContext = inputCurrentDocsContext;
  const model = editor.getModel();
  const variableModel = variablesEditor.getModel();
  if (
    !model ||
    (shouldManageVariables && !variableModel) ||
    !isGraphqlFieldPath(docsPath)
  ) {
    return null;
  }

  const isInOperation = (node: SelectionPathNode) =>
    !!getFieldInOperationHelper(currentDocsContext, node);

  const getFieldInOperation = (node: SelectionPathNode) =>
    getFieldInOperationHelper(currentDocsContext, node);

  const areAllFieldsSelected = (node: SelectionPathNode) => {
    const fields = getAllFields(node);
    if (
      currentDocsContext.matchType === 'full' ||
      currentDocsContext.matchType === 'root'
    ) {
      const selectionSetFieldNames = new Set(
        currentDocsContext.selectionSet.map((field) => field.name),
      );
      return fields.every((field) => selectionSetFieldNames.has(field.name));
    }
    return false;
  };

  const areAllTypeFieldsSelected = (type: SelectionPathNode) => {
    const docsContext = getCurrentDocsContext(
      model,
      editor.getPosition() ?? new monacoEditor.Position(1, 1),
      [...docsPath, type],
      schema,
    );
    const fields = getAllFields(type);
    if (docsContext.matchType === 'full' || docsContext.matchType === 'root') {
      const selectionSetFieldNames = new Set(
        docsContext.selectionSet.map((field) => field.name),
      );
      return fields.every((field) => selectionSetFieldNames.has(field.name));
    }
    return false;
  };

  const isAnyFieldSelected = () => {
    return currentDocsContext.matchType === 'root' ||
      currentDocsContext.matchType === 'full'
      ? !!currentDocsContext.selectionSet
      : false;
  };

  const isAnyTypeSelected = () => {
    return currentDocsContext.matchType === 'root' ||
      currentDocsContext.matchType === 'full'
      ? currentDocsContext.selectionSet.some(
          (selection) => selection.type === 'inline fragment',
        )
      : false;
  };

  const toggleInOperation = (node: SelectionPathNode) => {
    if (isInOperation(node)) {
      const deletion = getDeletion(
        currentDocsContext,
        new Set([node.name]),
        model,
        variableModel,
        shouldManageVariables,
      );
      executeEdits(deletion, editor, variablesEditor, setActiveEditorBottomTab);

      trackCustomEvent({
        action: 'delete_single_node',
        category: 'Explorer',
      });
    } else {
      const existingVariables = Object.keys(
        currentDocsContext.variableOperationCount,
      );
      const fullPath = [...currentDocsContext.remainingDocsPath, node];
      const fieldType = unwrapType(node);

      const childIndentLevel = currentDocsContext.indentLevel + fullPath.length;
      const { addedArguments, snippet } = printInnerSnippet(
        [],
        childIndentLevel,
        existingVariables,
        shouldManageVariables,
      );
      const childrenSnippet =
        snippet || isScalarType(fieldType) || isEnumType(fieldType)
          ? snippet
          : '  '.repeat(childIndentLevel);

      const insertion = getInsertion({
        childrenSnippet,
        childrenArguments: addedArguments,
        fullPath,
        currentDocsContext,
        model,
        variableModel,
        rootTypeNames: getRootTypeNames(schema),
        existingVariables,
        shouldManageVariables,
      });

      executeEdits(
        insertion,
        editor,
        variablesEditor,
        setActiveEditorBottomTab,
      );

      trackCustomEvent({
        action: 'insert_single_node',
        category: 'Explorer',
      });
    }
  };

  const insertAllCurrentScalars = (node: SelectionPathNode) => {
    const existingVariables = Object.keys(
      currentDocsContext.variableOperationCount,
    );
    const fullPath = currentDocsContext.remainingDocsPath;
    const fieldType = unwrapType(node);

    const fieldsToAdd = getAllCurrentScalars(node);
    // Remove existing fields from fieldsToAdd
    if (currentDocsContext.matchType === 'full') {
      currentDocsContext.selectionSet.forEach((field) => {
        const index = fieldsToAdd.findIndex((item) => {
          return item.name === field.name;
        });
        if (index > -1) {
          fieldsToAdd.splice(index, 1);
        }
      });
    }
    const childIndentLevel = currentDocsContext.indentLevel + fullPath.length;
    const { addedArguments, snippet } = printInnerSnippet(
      fieldsToAdd,
      childIndentLevel,
      existingVariables,
      shouldManageVariables,
    );
    const childrenSnippet =
      snippet || isScalarType(fieldType) || isEnumType(fieldType)
        ? snippet
        : '  '.repeat(childIndentLevel);

    const insertion = getInsertion({
      childrenSnippet,
      childrenArguments: addedArguments,
      fullPath,
      currentDocsContext,
      model,
      variableModel,
      rootTypeNames: getRootTypeNames(schema),
      existingVariables,
      shouldManageVariables,
    });
    executeEdits(insertion, editor, variablesEditor, setActiveEditorBottomTab);

    trackCustomEvent({
      action: 'insert_all_scalars',
      category: 'Explorer',
    });
  };

  const insertTypeScalars = (type: SelectionPathNode) => {
    const docsContext = getCurrentDocsContext(
      model,
      editor.getPosition() ?? new monacoEditor.Position(1, 1),
      [...docsPath, type],
      schema,
    );
    const existingVariables = Object.keys(docsContext.variableOperationCount);
    const fullPath = docsContext.remainingDocsPath;
    const fieldType = unwrapType(type);

    const fieldsToAdd = getAllCurrentScalars(type);
    // Remove existing fields from fieldsToAdd
    if (docsContext.matchType === 'full') {
      docsContext.selectionSet.forEach((field) => {
        const index = fieldsToAdd.findIndex((item) => {
          return item.name === field.name;
        });
        if (index > -1) {
          fieldsToAdd.splice(index, 1);
        }
      });
    }
    const childIndentLevel = docsContext.indentLevel + fullPath.length;
    const { addedArguments, snippet } = printInnerSnippet(
      fieldsToAdd,
      childIndentLevel,
      existingVariables,
      shouldManageVariables,
    );
    const childrenSnippet =
      snippet || isScalarType(fieldType) || isEnumType(fieldType)
        ? snippet
        : '  '.repeat(childIndentLevel);

    const insertion = getInsertion({
      childrenSnippet,
      childrenArguments: addedArguments,
      fullPath,
      currentDocsContext: docsContext,
      model,
      variableModel,
      rootTypeNames: getRootTypeNames(schema),
      existingVariables,
      shouldManageVariables,
    });
    executeEdits(insertion, editor, variablesEditor, setActiveEditorBottomTab);

    trackCustomEvent({
      action: 'insert_type_scalars',
      category: 'Explorer',
    });
  };

  const insertAllFieldsRecursively = (
    node: SelectionPathNode,
    includeNode: boolean,
  ) => {
    const existingVariables = Object.keys(
      currentDocsContext.variableOperationCount,
    );
    const fullPath = includeNode
      ? [...currentDocsContext.remainingDocsPath, node]
      : currentDocsContext.remainingDocsPath;
    const fieldType = unwrapType(node);

    const fieldsToAdd = getAllFields(node);
    // Remove existing fields from fieldsToAdd
    if (currentDocsContext.matchType === 'full') {
      currentDocsContext.selectionSet.forEach((field) => {
        const index = fieldsToAdd.findIndex((item) => {
          return item.name === field.name;
        });
        if (index > -1) {
          fieldsToAdd.splice(index, 1);
        }
      });
    }
    const childIndentLevel = currentDocsContext.indentLevel + fullPath.length;
    const { addedArguments, snippet } = printInnerSnippetRecursive(
      fieldsToAdd,
      childIndentLevel,
      existingVariables,
      shouldManageVariables,
    );
    const childrenSnippet =
      snippet || isScalarType(fieldType) || isEnumType(fieldType)
        ? snippet
        : '  '.repeat(childIndentLevel);

    const insertion = getInsertion({
      childrenSnippet,
      childrenArguments: addedArguments,
      fullPath,
      currentDocsContext,
      model,
      variableModel,
      rootTypeNames: getRootTypeNames(schema),
      existingVariables,
      shouldManageVariables,
    });
    executeEdits(insertion, editor, variablesEditor, setActiveEditorBottomTab);

    trackCustomEvent({
      action: 'insert_fields_recursively',
      category: 'Explorer',
    });
  };

  const insertTypeFieldsRecursively = (type: SelectionPathNode) => {
    const docsContext = getCurrentDocsContext(
      model,
      editor.getPosition() ?? new monacoEditor.Position(1, 1),
      [...docsPath, type],
      schema,
    );
    const existingVariables = Object.keys(docsContext.variableOperationCount);
    const fullPath = docsContext.remainingDocsPath;
    const fieldType = unwrapType(type);

    const fieldsToAdd = getAllFields(type);
    // Remove existing fields from fieldsToAdd
    if (docsContext.matchType === 'full') {
      docsContext.selectionSet.forEach((field) => {
        const index = fieldsToAdd.findIndex((item) => {
          return item.name === field.name;
        });
        if (index > -1) {
          fieldsToAdd.splice(index, 1);
        }
      });
    }
    const childIndentLevel = docsContext.indentLevel + fullPath.length;
    const { addedArguments, snippet } = printInnerSnippetRecursive(
      fieldsToAdd,
      childIndentLevel,
      existingVariables,
      shouldManageVariables,
    );
    const childrenSnippet =
      snippet || isScalarType(fieldType) || isEnumType(fieldType)
        ? snippet
        : '  '.repeat(childIndentLevel);

    const insertion = getInsertion({
      childrenSnippet,
      childrenArguments: addedArguments,
      fullPath,
      currentDocsContext: docsContext,
      model,
      variableModel,
      rootTypeNames: getRootTypeNames(schema),
      existingVariables,
      shouldManageVariables,
    });
    executeEdits(insertion, editor, variablesEditor, setActiveEditorBottomTab);

    trackCustomEvent({
      action: 'insert_type_recursively',
      category: 'Explorer',
    });
  };

  const toggleAllFields = (node: SelectionPathNode) => {
    if (isAnyFieldSelected()) {
      const deletion = getAllFieldsDeletion(
        currentDocsContext,
        model,
        variableModel,
        shouldManageVariables,
      );
      executeEdits(deletion, editor, variablesEditor, setActiveEditorBottomTab);

      trackCustomEvent({
        action: 'delete_all_fields',
        category: 'Explorer',
      });
    } else {
      const existingVariables = Object.keys(
        currentDocsContext.variableOperationCount,
      );
      const fullPath = currentDocsContext.remainingDocsPath;
      const fieldType = unwrapType(node);

      const fieldsToAdd = getAllFields(node);
      // Remove existing fields from fieldsToAdd
      if (currentDocsContext.matchType === 'full') {
        currentDocsContext.selectionSet.forEach((field) => {
          const index = fieldsToAdd.findIndex((item) => {
            return item.name === field.name;
          });
          if (index > -1) {
            fieldsToAdd.splice(index, 1);
          }
        });
      }
      const childIndentLevel = currentDocsContext.indentLevel + fullPath.length;
      const { addedArguments, snippet } = printInnerSnippet(
        fieldsToAdd,
        childIndentLevel,
        existingVariables,
        shouldManageVariables,
      );
      const childrenSnippet =
        snippet || isScalarType(fieldType) || isEnumType(fieldType)
          ? snippet
          : '  '.repeat(childIndentLevel);

      const insertion = getInsertion({
        childrenSnippet,
        childrenArguments: addedArguments,
        fullPath,
        currentDocsContext,
        model,
        variableModel,
        rootTypeNames: getRootTypeNames(schema),
        existingVariables,
        shouldManageVariables,
      });
      executeEdits(
        insertion,
        editor,
        variablesEditor,
        setActiveEditorBottomTab,
      );

      trackCustomEvent({
        action: 'insert_all_fields',
        category: 'Explorer',
      });
    }
  };

  const deleteAllTypes = (types: Array<SelectionPathNode>) => {
    const deletion = getDeletion(
      currentDocsContext,
      new Set(types.map((type) => type.name)),
      model,
      variableModel,
      shouldManageVariables,
    );
    executeEdits(deletion, editor, variablesEditor, setActiveEditorBottomTab);

    trackCustomEvent({
      action: 'delete_types',
      category: 'Explorer',
    });
  };

  const insertTypeFields = (type: SelectionPathNode) => {
    const docsContext = getCurrentDocsContext(
      model,
      editor.getPosition() ?? new monacoEditor.Position(1, 1),
      [...docsPath, type],
      schema,
    );

    const existingVariables = Object.keys(docsContext.variableOperationCount);
    const fullPath = docsContext.remainingDocsPath;
    const fieldType = unwrapType(type);

    const fieldsToAdd = getAllFields(type);
    // Remove existing fields from fieldsToAdd
    if (docsContext.matchType === 'full') {
      docsContext.selectionSet.forEach((field) => {
        const index = fieldsToAdd.findIndex((item) => {
          return item.name === field.name;
        });
        if (index > -1) {
          fieldsToAdd.splice(index, 1);
        }
      });
    }
    const childIndentLevel = docsContext.indentLevel + fullPath.length;
    const { addedArguments, snippet } = printInnerSnippet(
      fieldsToAdd,
      childIndentLevel,
      existingVariables,
      shouldManageVariables,
    );
    const childrenSnippet =
      snippet || isScalarType(fieldType) || isEnumType(fieldType)
        ? snippet
        : '  '.repeat(childIndentLevel);

    const insertion = getInsertion({
      childrenSnippet,
      childrenArguments: addedArguments,
      fullPath,
      currentDocsContext: docsContext,
      model,
      variableModel,
      rootTypeNames: getRootTypeNames(schema),
      existingVariables,
      shouldManageVariables,
    });
    executeEdits(insertion, editor, variablesEditor, setActiveEditorBottomTab);

    trackCustomEvent({
      action: 'insert_type_fields',
      category: 'Explorer',
    });
  };

  const getArgumentInOperation = (argumentDocNode: GraphQLArgumentNode) => {
    const fieldNode = docsPath[docsPath.length - 1] as GraphQLNode | undefined;
    const fieldToggle = getFieldToggle(
      getCurrentDocsContext(
        model,
        editor.getPosition() ?? new monacoEditor.Position(1, 1),
        docsPath.slice(0, -1),
        schema,
      ),
      docsPath.slice(0, -1),
      schema,
      editor,
      variablesEditor,
      variablesEditorPosition,
      setActiveEditorBottomTab,
      shouldManageVariables,
      trackCustomEvent,
    );
    const fieldInOperation =
      fieldNode &&
      (fieldNode instanceof GraphQLObjectType || 'args' in fieldNode)
        ? fieldToggle?.getFieldInOperation(fieldNode)
        : undefined;

    const argumentFromDocsContext = currentDocsContext.arguments.find(
      (arg) => arg.name === argumentDocNode.name,
    );
    return argumentFromDocsContext &&
      fieldInOperation &&
      fieldInOperation.range.containsRange(argumentFromDocsContext.range)
      ? argumentFromDocsContext
      : undefined;
  };

  const isArgumentPathValueSet = (argumentPath: GraphQLArgumentNode[]) => {
    const argument =
      (isMinLengthArray(1, argumentPath) &&
        getArgumentInOperation(argumentPath[0])) ||
      undefined;

    if (argument && argumentPath.length === 1) return true;
    return !!(
      argument?.isVariable &&
      variableModel &&
      get(
        jsoncParser.parse(variablesEditor.getValue() || '{}'),
        docsPathToVariableJSONPath(
          argumentPath,
          argument.value,
          variablesEditorPosition,
          variableModel,
        ),
      ) !== undefined
    );
  };

  const toggleArgumentPathInOperation = (
    argumentPath: GraphQLArgumentNode[],
  ) => {
    let argumentPossiblyAdded = false;
    const fieldNode = docsPath[docsPath.length - 1] as GraphQLNode | undefined;
    if (!fieldNode) return;
    const fieldToggle = getFieldToggle(
      getCurrentDocsContext(
        model,
        editor.getPosition() ?? new monacoEditor.Position(1, 1),
        docsPath.slice(0, -1),
        schema,
      ),
      docsPath.slice(0, -1),
      schema,
      editor,
      variablesEditor,
      variablesEditorPosition,
      setActiveEditorBottomTab,
      shouldManageVariables,
      trackCustomEvent,
    );
    let fieldInOperation =
      (fieldNode instanceof GraphQLObjectType || 'args' in fieldNode) &&
      fieldToggle?.isInOperation(fieldNode);

    if (!fieldInOperation && 'args' in fieldNode) {
      argumentPossiblyAdded = true;
      // Field does not exist. Toggle it and then create
      // a new context and try to toggle the argument again.
      fieldToggle?.toggleInOperation(fieldNode);
      fieldInOperation =
        (fieldNode instanceof GraphQLObjectType || 'args' in fieldNode) &&
        getFieldToggle(
          getCurrentDocsContext(
            model,
            editor.getPosition() ?? new monacoEditor.Position(1, 1),
            docsPath.slice(0, -1),
            schema,
          ),
          docsPath.slice(0, -1),
          schema,
          editor,
          variablesEditor,
          variablesEditorPosition,
          setActiveEditorBottomTab,
          shouldManageVariables,
          trackCustomEvent,
        )?.isInOperation(fieldNode);
      currentDocsContext = getCurrentDocsContext(
        model,
        editor.getPosition() ?? new monacoEditor.Position(1, 1),
        docsPath,
        schema,
      );
    }

    if (!fieldInOperation) return; // Field does not exist. But we can't toggle it

    let argument =
      isMinLengthArray(1, argumentPath) &&
      getArgumentInOperation(argumentPath[0]);
    if (!argument && isMinLengthArray(1, argumentPath)) {
      argumentPossiblyAdded = true;
      // Field in operation but no arugment
      const insertion = getArgumentsInsertion(
        currentDocsContext,
        argumentPath[0],
        model,
        variableModel,
        Object.keys(currentDocsContext.variableOperationCount),
        fieldNode.name,
        shouldManageVariables,
      );
      executeEdits(
        insertion,
        editor,
        variablesEditor,
        setActiveEditorBottomTab,
      );
      currentDocsContext = getCurrentDocsContext(
        model,
        editor.getPosition() ?? new monacoEditor.Position(1, 1),
        docsPath,
        schema,
      );
      argument = getArgumentInOperation(argumentPath[0]);
    }
    if (!argument) return; // no argument and failed to add it

    const currentJsonValue = variablesEditor.getValue();

    if (!argumentPossiblyAdded && isArgumentPathValueSet(argumentPath)) {
      if (argumentPath.length === 1 && isMinLengthArray(1, argumentPath)) {
        // deleting top level argument, delete argument entirely
        executeEdits(
          getArgumentDeletion(
            currentDocsContext,
            argumentPath[0].name,
            variableModel,
            shouldManageVariables,
          ),
          editor,
          variablesEditor,
          setActiveEditorBottomTab,
        );
      } else if (argument.isVariable) {
        const variableJSON: JSONValue =
          jsoncParser.parse(currentJsonValue || '{}') || {};
        if (
          typeof variableJSON !== 'object' ||
          !variableJSON ||
          Array.isArray(variableJSON) ||
          !variableModel
        ) {
          return;
        }

        unset(
          variableJSON,
          docsPathToVariableJSONPath(
            argumentPath,
            argument.value,
            variablesEditorPosition,
            variableModel,
          ),
        );

        executeEdits(
          {
            type: 'deleteArgument',
            variableEdits: [
              jsoncParser.modify(
                currentJsonValue,
                [argument.value],
                variableJSON[argument.value],
                { formattingOptions: { insertSpaces: true, tabSize: 2 } },
              ),
            ],
          },
          editor,
          variablesEditor,
          setActiveEditorBottomTab,
        );
      }
    } else if (argument.isVariable && !isArgumentPathValueSet(argumentPath)) {
      const variableJSON: JSONValue =
        jsoncParser.parse(currentJsonValue || '{}') || {};
      if (
        typeof variableJSON !== 'object' ||
        !variableJSON ||
        Array.isArray(variableJSON) ||
        !variableModel
      ) {
        return;
      }

      const jsonPath = docsPathToVariableJSONPath(
        argumentPath,
        argument.value,
        variablesEditorPosition,
        variableModel,
      );

      set(variableJSON, jsonPath, null);
      executeEdits(
        {
          type: 'insertArgument',
          variableEdits: [
            jsoncParser.modify(
              currentJsonValue,
              [argument.value],
              variableJSON[argument.value],
              { formattingOptions: { insertSpaces: true, tabSize: 2 } },
            ),
          ],
        },
        editor,
        variablesEditor,
        setActiveEditorBottomTab,
      );
    }
  };

  return {
    isInOperation,
    getFieldInOperation,
    isAnyFieldSelected,
    isAnyTypeSelected,
    areAllFieldsSelected,
    areAllTypeFieldsSelected,
    toggleInOperation,
    toggleAllFields,
    insertTypeFields,
    insertAllCurrentScalars,
    insertTypeScalars,
    insertAllFieldsRecursively,
    insertTypeFieldsRecursively,
    getArgumentInOperation,
    isArgumentPathValueSet,
    toggleArgumentPathInOperation,
    deleteAllTypes,
  };
}
